import { useState, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../home/components/Navbar";
import Footer from "../../home/components/Footer";
import { getVisaBySlug } from "../../../mocks/visas";

const FORM_URL = "https://readdy.ai/api/form/d725cvplum2co2q1g2jg";

export default function VisaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const visa = slug ? getVisaBySlug(slug) : undefined;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [applicants, setApplicants] = useState("1");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!visa) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center px-6">
          <i className="ri-error-warning-line text-5xl text-gray-300 mb-4 block"></i>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Visa Not Found</h1>
          <p className="text-gray-500 text-sm mb-6">This visa page doesn&apos;t exist or has been removed.</p>
          <button
            type="button"
            onClick={() => navigate("/visa")}
            className="bg-cyan-600 text-white px-6 py-2.5 rounded-md text-sm font-semibold cursor-pointer hover:bg-cyan-700 transition-colors whitespace-nowrap"
          >
            View All Visas
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!phone.trim()) { setError("Please enter your phone number."); return; }
    if (!city.trim()) { setError("Please enter your city."); return; }
    if (!travelDate) { setError("Please select your travel date."); return; }
    if (message.length > 500) { setError("Message must be 500 characters or fewer."); return; }

    setError("");
    setLoading(true);
    try {
      const body = new URLSearchParams({
        "Visa Type": visa.title,
        "Country": visa.country,
        "Your Name": name,
        "Phone": `+92${phone}`,
        "Email": email || "Not provided",
        "City": city,
        "Travel Date": travelDate,
        "No. of Applicants": applicants,
        "Message": message || "—",
        "Visa Fee (PKR)": visa.price,
      });
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <div className="pt-16">
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <img
            src={visa.heroImage}
            alt={visa.country}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          {/* Amber strip */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-400" />
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-7 md:px-12">
            <div className="max-w-7xl mx-auto">
              <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-1">
                <a href="/visa" className="hover:underline cursor-pointer">Visa Services</a>
                <span className="mx-2 text-white/50">/</span>
                {visa.country}
              </p>
              <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight">
                {visa.title}
              </h1>
              <p className="text-white/80 text-sm mt-1">{visa.tagline}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="bg-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 divide-y-2 md:divide-y-0 md:divide-x divide-white/20">
          {[
            { label: "Fee (PKR)", value: visa.price },
            { label: "Processing", value: visa.processingTime },
            { label: "Validity", value: visa.validity },
            { label: "Max Stay", value: visa.stayDuration },
          ].map(item => (
            <div key={item.label} className="text-center px-4 py-1 md:py-0">
              <p className="text-white/70 text-xs font-medium">{item.label}</p>
              <p className="text-white font-bold text-sm mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-10">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* ── Left: Info ── */}
          <div className="flex-1 min-w-0">
            {/* Description */}
            <h2 className="text-xl font-bold text-gray-900 mb-3">About {visa.country} Visa</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">{visa.description}</p>

            {/* Country image */}
            <div className="w-full h-52 rounded-xl overflow-hidden mb-6">
              <img
                src={visa.bannerImage}
                alt={visa.country}
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Highlights */}
            <div className="bg-cyan-50 rounded-xl p-5 mb-6">
              <h3 className="text-sm font-bold text-cyan-700 mb-3 uppercase tracking-wide">Why Apply Through Us</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {visa.highlights.map(h => (
                  <div key={h} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-cyan-600 text-base"></i>
                    </div>
                    <span className="text-sm text-gray-700">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <h3 className="text-base font-bold text-gray-900 mb-3">Document Requirements</h3>
            <div className="space-y-2 mb-6">
              {visa.requirements.map((req, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <span className="w-5 h-5 flex items-center justify-center bg-amber-100 text-amber-700 text-xs font-bold rounded-full flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-700">{req}</span>
                </div>
              ))}
            </div>

            {/* Extra info */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Good to Know</p>
              <p className="text-sm text-gray-700 leading-relaxed">{visa.extraInfo}</p>
            </div>
          </div>

          {/* ── Right: Inquiry Form ── */}
          <div className="w-full lg:w-96 flex-shrink-0">
            <div className="sticky top-20">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-5 py-4">
                  <h3 className="text-white font-bold text-base">Apply for {visa.country} Visa</h3>
                  <p className="text-cyan-100 text-xs mt-0.5">Fill in your details and our team will get back to you within 24 hours</p>
                </div>

                {submitted ? (
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">Inquiry Sent!</h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Our team will contact you within 24 hours with complete guidance for your {visa.country} visa.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSubmitted(false);
                        setName(""); setPhone(""); setEmail(""); setCity("");
                        setTravelDate(""); setApplicants("1"); setMessage("");
                      }}
                      className="text-sm text-cyan-600 font-semibold cursor-pointer hover:underline whitespace-nowrap"
                    >
                      Submit Another
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input
                        name="name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Phone No. <span className="text-red-500">*</span></label>
                      <div className="flex items-center border border-gray-200 rounded-md overflow-hidden focus-within:border-cyan-400 transition-colors">
                        <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-sm text-gray-600 whitespace-nowrap select-none">+92</span>
                        <input
                          name="phone"
                          value={phone}
                          onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="3001234567"
                          maxLength={10}
                          className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Email <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Your City <span className="text-red-500">*</span></label>
                      <input
                        name="city"
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        placeholder="e.g. Lahore"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>

                    {/* Travel Date + Applicants */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Travel Date <span className="text-red-500">*</span></label>
                        <input
                          name="travelDate"
                          type="date"
                          value={travelDate}
                          onChange={e => setTravelDate(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Applicants</label>
                        <select
                          name="applicants"
                          value={applicants}
                          onChange={e => setApplicants(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer bg-white appearance-none"
                        >
                          {["1","2","3","4","5","6","7","8","9","10+"].map(n => (
                            <option key={n} value={n}>{n} Person{n !== "1" ? "s" : ""}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Message <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        name="message"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        maxLength={500}
                        rows={3}
                        placeholder="Any questions or special requirements..."
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right mt-0.5">{message.length}/500</p>
                    </div>

                    {/* Fee note */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5">
                      <span className="text-xs text-gray-500">Service Fee (per person)</span>
                      <span className="text-sm font-bold text-green-600">PKR {visa.price}</span>
                    </div>

                    {error && (
                      <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-md cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-send-plane-fill text-base"></i>
                      {loading ? "Sending..." : "SEND INQUIRY"}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      Our team responds within 24 hours · 03711432451
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
